import type { GeneratedDefinition, GeneratedStatus } from "@keydials/api-client";
import { defaultDimension, RAG_PALETTE, uniqueKey, WORKFLOW_PALETTE } from "./wizardSupport";
import { slug } from "./keys";

// ---------------------------------------------------------------------------
// The builder's colours (KEY-560): three presets, and the handful of edits "Customise" allows. A checklist dial has one
// scoring scale, created silently from these colours and kept in step with them, so the author only ever sees "colours".
// Every function takes the model and returns a new one; nothing is mutated.
// ---------------------------------------------------------------------------

export type BuilderPresetId = "traffic-light" | "to-do-and-done" | "workflow";

export interface BuilderPreset {
  id: BuilderPresetId;
  /** The palette's stored name. The card's wording comes from the user app's copy. */
  name: string;
  statuses: readonly GeneratedStatus[];
}

/** Two states for a list that is simply ticked off. New with the builder; the admin wizard offers the other two. */
export const TODO_DONE_PALETTE: { name: string; statuses: GeneratedStatus[] } = {
  name: "To do and done",
  statuses: [
    { key: "to-do", name: "To do", colour: "#94a3b8", order: 0, allowedRoles: ["Editor", "Reviewer"], isTerminal: false },
    { key: "done", name: "Done", colour: "#16a34a", order: 1, allowedRoles: ["Editor", "Reviewer"], isTerminal: true },
  ],
};

/** In the order the Colours stage shows them, the first being the recommended one. */
export const BUILDER_PRESETS: readonly BuilderPreset[] = [
  // The wizard calls this "Traffic light (RAG)"; a customer doesn't know the abbreviation.
  { id: "traffic-light", name: "Traffic light", statuses: RAG_PALETTE.statuses },
  { id: "to-do-and-done", name: TODO_DONE_PALETTE.name, statuses: TODO_DONE_PALETTE.statuses },
  { id: "workflow", name: WORKFLOW_PALETTE.name, statuses: WORKFLOW_PALETTE.statuses },
];

const cloneStatuses = (statuses: readonly GeneratedStatus[]): GeneratedStatus[] =>
  statuses.map((s) => ({ ...s, allowedRoles: [...s.allowedRoles] }));

/**
 * Replaces the dial's states with a preset's. Rules or reset schedules a template brought that name the old states are not
 * rewritten; the readiness check reports them.
 */
export function applyPreset(model: GeneratedDefinition, id: BuilderPresetId): GeneratedDefinition {
  const preset = BUILDER_PRESETS.find((p) => p.id === id);
  if (!preset) return model;
  return syncStatusDimension({ ...model, statusSet: { name: preset.name, statuses: cloneStatuses(preset.statuses) } });
}

/** Which preset the dial's states still match exactly, or "custom" once anything has been changed. */
export function currentPreset(model: GeneratedDefinition): BuilderPresetId | "custom" {
  const matches = (preset: BuilderPreset) =>
    model.statusSet.statuses.length === preset.statuses.length &&
    model.statusSet.statuses.every((s, i) => {
      const p = preset.statuses[i];
      return (
        s.key === p.key &&
        s.name === p.name &&
        s.colour.toLowerCase() === p.colour.toLowerCase() &&
        s.order === p.order &&
        Boolean(s.isTerminal) === Boolean(p.isTerminal)
      );
    });
  return BUILDER_PRESETS.find(matches)?.id ?? "custom";
}

/**
 * A checklist dial's one scoring scale, in the dial's own colours: created when there is none, and kept in step when there is
 * exactly one. A dial with several scales came from a template the builder doesn't model, so it is left alone, and so is a
 * dial with one colour per action point, which has none.
 */
export function syncStatusDimension(model: GeneratedDefinition): GeneratedDefinition {
  if (model.kind !== "Itemised") return model;
  const dimensions = model.dimensions ?? [];
  if (dimensions.length === 0) return { ...model, dimensions: [defaultDimension(scaleStatuses(model.statusSet.statuses, []))] };
  if (dimensions.length > 1) return model;
  const [only] = dimensions;
  return { ...model, dimensions: [{ ...only, palette: { name: only.palette.name, statuses: scaleStatuses(model.statusSet.statuses, only.palette.statuses) } }] };
}

/**
 * The dial's states, as the scale's. Everything follows the dial except the fields a state requires: those name fields of the
 * level the palette scores (the dial's states gate action points, the scale's gate items), so each scale state keeps the gate
 * it had under the same key, and never takes the dial's.
 */
function scaleStatuses(statuses: readonly GeneratedStatus[], current: readonly GeneratedStatus[]): GeneratedStatus[] {
  const gates = new Map(current.map((s) => [s.key, s.requiredAttributeKeys]));
  return cloneStatuses(statuses).map((s) => {
    const own = gates.get(s.key);
    const next = { ...s };
    delete next.requiredAttributeKeys;
    return own && own.length > 0 ? { ...next, requiredAttributeKeys: [...own] } : next;
  });
}

const withStatuses = (model: GeneratedDefinition, statuses: GeneratedStatus[]): GeneratedDefinition =>
  syncStatusDimension({ ...model, statusSet: { ...model.statusSet, statuses } });

const mapStatus = (model: GeneratedDefinition, key: string, change: (s: GeneratedStatus) => GeneratedStatus) =>
  withStatuses(model, model.statusSet.statuses.map((s) => (s.key === key ? change(s) : s)));

/** Adds a state after the others, able to be set by whoever could set the last one. Returns its key. */
export function addStatus(model: GeneratedDefinition, name: string, colour: string): { model: GeneratedDefinition; key: string } {
  const statuses = model.statusSet.statuses;
  const key = uniqueKey(slug(name, "state"), new Set(statuses.map((s) => s.key)));
  const order = statuses.reduce((max, s) => Math.max(max, s.order), -1) + 1;
  const allowedRoles = statuses.length > 0 ? [...statuses[statuses.length - 1].allowedRoles] : ["Editor", "Reviewer"];
  return { model: withStatuses(model, [...statuses, { key, name, colour, order, allowedRoles, isTerminal: false }]), key };
}

export const renameStatus = (model: GeneratedDefinition, key: string, name: string) => mapStatus(model, key, (s) => ({ ...s, name }));

export const recolourStatus = (model: GeneratedDefinition, key: string, colour: string) =>
  mapStatus(model, key, (s) => ({ ...s, colour }));

/** "Counts as done": what fills the centre ring. A state that counts can't also be left out of the count. */
export const setCountsAsDone = (model: GeneratedDefinition, key: string, done: boolean) =>
  mapStatus(model, key, (s) => ({ ...s, isTerminal: done, ...(done ? { isExcludedFromRollup: false } : {}) }));

/** Removes a state. The last one stays: a dial needs something to set. */
export function removeStatus(model: GeneratedDefinition, key: string): GeneratedDefinition {
  if (model.statusSet.statuses.length <= 1) return model;
  return withStatuses(model, model.statusSet.statuses.filter((s) => s.key !== key));
}
